import {
  FoodDetailsContent,
  FoodDetailsHeader,
  FoodDetailsInfo,
  StyledFoodDetails,
} from "./styled"
import { BackButton } from "components/Common/BackButton"
import NutrientInformation from "components/foodDetails/share/NutrientInformation"
import Button from "components/Buttons/Button"
import FoodImage from "components/foodDetails/share/FoodImage"
import { useState } from "react"
import { useBackToHome } from "features/meal/hooks"
import ChevronLeftBtn from "components/Icons/ChevronLeftBtn"
import ScheduleConfigFormSection from "components/foodDetails/share/ScheduleConfigFormSection"
import { useCreateSchedule } from "features/schedule/hooks/CRUDSchedule"
import useScheduleFormSection from "features/schedule/hooks/useScheduleFormSection"
import { validateScheduleConfig } from "utils/validator"
import PopConfirm from "components/PopConfirm"
import useEdamaForm from "features/food/hooks/edamaFood/useEdamaDetailsForm"
import EdamaNameAndPortion from "../share/EdamaNameAndPortion"
import useLogMealFromFood from "features/meal/hooks/logMeal/useLogMealFromFood"
import { IEdamaFood } from "model/food/types/edama.types"
import AppStyleCtaBox from "components/Common/AppStyleCtaBox"
import { TopNavWrapper } from "components/Layouts/TopNavWrapper"
import { FoodCreationMethod } from "model/food/types/mealPayload.types"
import EditNutrientForm from "../share/EditNutrientForm"
import EditableNutrientInformation from "../share/EditableNutrientInformation"

interface IProps {
  onBack: () => void
  food: IEdamaFood
  foodCreationMethod?: FoodCreationMethod
}

const EdamaFoodDetailsWithSchedule = (props: IProps) => {
  const [displaySection, setDisplaySection] = useState<
    "FOOD_FORM" | "EDIT_NUTRITION_FORM"
  >("FOOD_FORM")

  const { onBack, food, foodCreationMethod } = props
  const { edama, onPortionChange, onUnitChange, updateFoodAsManualNutrition } =
    useEdamaForm(food)

  const [isPortionValid, setIsPortionValid] = useState(true)
  const [hasSchedule, setHasSchedule] = useState(false)

  const onPortionChangeHandler = (value: number) => {
    onPortionChange(value)
    setIsPortionValid(true)
  }

  const {
    onDayOfWeekChange,
    onSchedulePeriodChange,
    onScheduleTypeChange,
    scheduleConfig,
    isNoEndDate,
    onNoEndDateChange,

    getCreateSchedulePayloadFromFood,
  } = useScheduleFormSection()

  const backToHome = useBackToHome()
  const { handleLogMealFromFood, logMealLoading } =
    useLogMealFromFood(backToHome)

  const { handleCreateSchedule, createScheduleLoading } =
    useCreateSchedule(backToHome)

  const [isPopConfirmScheduleOpen, setIsPopConfirmScheduleOpen] =
    useState(false)

  const onSubmit = (food: IEdamaFood) => {
    if (hasSchedule) {
      const schedulePayload = getCreateSchedulePayloadFromFood(food)

      if (schedulePayload) {
        handleCreateSchedule(schedulePayload, foodCreationMethod)
      }
    } else {
      handleLogMealFromFood(food, foodCreationMethod)
    }
  }

  const isScheduleValid = hasSchedule
    ? validateScheduleConfig(scheduleConfig, !isNoEndDate)
    : true

  if (displaySection === "EDIT_NUTRITION_FORM") {
    return (
      <EditNutrientForm
        onClose={() => {
          setDisplaySection("FOOD_FORM")
        }}
        meal={edama}
        onSubmit={updateFoodAsManualNutrition}
      />
    )
  }

  return (
    <StyledFoodDetails>
      <FoodDetailsContent>
        <FoodImage previewImageUrl={food.previewImageUrl}>
          <TopNavWrapper>
            <FoodDetailsHeader>
              <BackButton onClick={onBack}>
                <ChevronLeftBtn />
              </BackButton>
            </FoodDetailsHeader>
          </TopNavWrapper>
        </FoodImage>

        <FoodDetailsInfo>
          <EdamaNameAndPortion
            edama={edama}
            unitValue={edama.unit}
            portionValue={edama.portion}
            onPortionChange={onPortionChangeHandler}
            onUnitChange={onUnitChange}
            unitOptions={edama.units}
            setIsPortionValid={setIsPortionValid}
            calories={edama.calorie}
            gram={edama.gram}
          />

          <EditableNutrientInformation
            entry={edama}
            setEditMode={() => {
              setDisplaySection("EDIT_NUTRITION_FORM")
            }}
          />

          <ScheduleConfigFormSection
            scheduleConfig={scheduleConfig}
            onDayOfWeekChange={onDayOfWeekChange}
            onSchedulePeriodChange={onSchedulePeriodChange}
            onScheduleTypeChange={onScheduleTypeChange}
            isNoEndDate={isNoEndDate}
            onNoEndDateChange={onNoEndDateChange}
            hasSchedule={hasSchedule}
            setHasSchedule={setHasSchedule}
          />
        </FoodDetailsInfo>
      </FoodDetailsContent>
      <AppStyleCtaBox>
        <Button
          onClick={() => {
            if (hasSchedule) {
              setIsPopConfirmScheduleOpen(true)
            } else {
              onSubmit(edama)
            }
          }}
          style={{ width: "100%" }}
          $type="primary"
          disabled={!isPortionValid || !isScheduleValid}
          loading={logMealLoading}
        >
          Log this item
        </Button>
      </AppStyleCtaBox>

      <PopConfirm
        cancelText="No"
        confirmText="Yes"
        isOpen={isPopConfirmScheduleOpen}
        title="Add Scheduled Meal?"
        onCancel={() => setIsPopConfirmScheduleOpen(false)}
        onConfirm={() => {
          onSubmit(edama)
        }}
        type="info"
        loading={createScheduleLoading}
      >
        <div style={{ textAlign: "center" }}>
          This meal will be logged automatically on your selected days. Do you
          want to proceed?
        </div>
      </PopConfirm>
    </StyledFoodDetails>
  )
}

export default EdamaFoodDetailsWithSchedule
